/**
 * Update Article (Bulk Brief Creator): which rows can be updated, why the others
 * can't, and whether the plan allows the batch.
 *
 * The row rules mirror the update-article-queue job creator
 * (SEOwind_functions services/update-article-queue/optimize_request.py) so the
 * dialog can explain a skip before anything is sent. The queue checks every row
 * again — this is for the dialog, not the gate.
 *
 * No imports, so it runs under Node's built-in test runner.
 */

export const MAX_UPDATE_COMMENT_CHARS = 20000;

export interface UpdateArticleRowFields {
  updateArticle?: string | null;
  keyword?: string | null;
  projectId?: string | null;
  projectDomain?: string | null;
  articleInstructions?: string | null;
  // A finished SEOwind article is an update source too: `articleId` is set only
  // when the article is written (status "created"), so pyseowind can read it from
  // the system instead of scraping a live page.
  articleId?: string | null;
  articleStatus?: string | null;
}

export type UpdateArticleSkipCode =
  | 'no_update_source'
  | 'no_update_url'
  | 'no_keyword'
  | 'no_project'
  | 'no_project_domain'
  | 'comment_too_long'
  | 'duplicate_row'
  | 'no_row_id'
  | 'update_in_progress'
  | 'update_finished';

const SKIP_TEXT: Record<UpdateArticleSkipCode, string> = {
  no_update_source: 'No Update Article URL and no finished SEOwind article to update',
  no_update_url: 'Update Article URL is not a valid http(s) link',
  no_keyword: 'No keyword',
  no_project: 'No matching project for this row',
  no_project_domain: 'Project has no domain',
  comment_too_long: `Article-specific comment is over ${MAX_UPDATE_COMMENT_CHARS.toLocaleString('en-US')} characters`,
  duplicate_row: 'Row selected twice',
  no_row_id: 'Row has no id',
  update_in_progress: 'An earlier update of this article is still running',
  update_finished: 'This update has already finished',
};

/** Human wording for a skip code from this module or from the queue. */
export function updateArticleSkipText(code: string): string {
  return SKIP_TEXT[code as UpdateArticleSkipCode] || code;
}

/** A real, parseable http(s) page with a host — cross-domain is fine, `https://`
 * and malformed URLs are not. Mirrors the queue's `_has_host`. */
function hasHost(url: string): boolean {
  try {
    const u = new URL(url);
    return (u.protocol === 'http:' || u.protocol === 'https:') && Boolean(u.hostname);
  } catch {
    return false;
  }
}

/** A finished SEOwind article can be read from the system by its id. */
function hasInternalArticle(row: UpdateArticleRowFields): boolean {
  return Boolean(String(row.articleId || '').trim()) && String(row.articleStatus || '') === 'created';
}

/** True for a SEOwind app host — the app runs on seowind.io, SEOwind's own Amplify
 * app (id `dim81hk9mdqod`, per src/utils/urls.ts), or localhost. An article-editor
 * path on any OTHER host — including a different customer's `*.amplifyapp.com` site —
 * is a foreign page to scrape, not a SEOwind article. */
const SEOWIND_AMPLIFY_APP = 'dim81hk9mdqod.amplifyapp.com';
function isSeowindAppHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^www\./, '');
  return (
    host === 'seowind.io' ||
    host.endsWith('.seowind.io') ||
    host === SEOWIND_AMPLIFY_APP ||
    host.endsWith(`.${SEOWIND_AMPLIFY_APP}`) ||
    host === 'localhost'
  );
}

/**
 * The SEOwind article id in an in-app article editor URL
 * (`https://<seowind host>/app/dashboard/articleEditor/?id=<id>`), or null for
 * anything else. A SEOwind link is an internal source read from the system, not a
 * page to scrape. It must be an http(s) URL on a SEOwind host — an article-editor
 * path on another host (or a non-http URL) is a normal external page. `optimizedEditor`
 * links are deliberately NOT matched — their id is an optimize-run id, not an article id.
 */
export function seowindArticleId(url: string | null | undefined): string | null {
  const text = String(url || '').trim();
  if (!text) return null;
  try {
    const u = new URL(text);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!isSeowindAppHost(u.hostname)) return null;
    if (!/\/dashboard\/articleEditor\/?$/i.test(u.pathname)) return null;
    const id = (u.searchParams.get('id') || '').trim();
    return id || null;
  } catch {
    return null;
  }
}

/**
 * Two source kinds share the Update Article cell:
 *   - external: a live web page to scrape — keyword and project must come from the
 *     row (the row's Client Website resolves the project);
 *   - internal: a SEOwind article read from the system, either a pasted
 *     `articleEditor` link or the row's own finished article (blank cell) — keyword,
 *     location and project are taken from the article's brief, so the row need not
 *     carry them. The proxy authorizes and fills those from the article.
 */
export function updateArticleSkipCode(row: UpdateArticleRowFields): UpdateArticleSkipCode | null {
  const url = String(row.updateArticle || '').trim();
  const linkedId = seowindArticleId(url);
  const internal = Boolean(linkedId) || (!url && hasInternalArticle(row));

  if (url && !linkedId) {
    if (!hasHost(url)) return 'no_update_url';
  } else if (!url && !hasInternalArticle(row)) {
    return 'no_update_source';
  }
  // External (scrape) rows must carry a keyword and a resolvable project; internal
  // rows derive both from the article's brief.
  if (!internal) {
    if (!String(row.keyword || '').trim()) return 'no_keyword';
    if (!String(row.projectId || '').trim()) return 'no_project';
    if (!String(row.projectDomain || '').trim()) return 'no_project_domain';
  }
  // Count characters the way the queue (Python) does, not UTF-16 units.
  if (Array.from(String(row.articleInstructions || '')).length > MAX_UPDATE_COMMENT_CHARS) {
    return 'comment_too_long';
  }
  return null;
}

export interface UpdatePlanFields {
  numberOfArticlesInPlan?: number | string | null;
  totalArticlesWritten?: number | string | null;
  numberOfWordsInPlan?: number | string | null;
  totalWordsWritten?: number | string | null;
}

export type UpdatePlanCheck =
  | { kind: 'ok'; articlesLeft: number }
  | { kind: 'no-plan' }
  | { kind: 'limit-reached'; message: string }
  | { kind: 'too-many'; articlesLeft: number };

/**
 * The same limits the Content update screen checks before an update, applied to a
 * batch: nothing left stops it (and shows the same upgrade message); fewer
 * updates left than rows selected stops it too, instead of overdrawing the plan.
 *
 * @param payload the `getActivePlan` thunk payload
 */
export function checkUpdatePlan(
  payload: { plan?: UpdatePlanFields | null; error?: unknown } | null | undefined,
  rowCount: number
): UpdatePlanCheck {
  const plan = payload?.plan;
  if (!plan || Object.keys(plan).length === 0 || payload?.error) {
    return { kind: 'no-plan' };
  }
  const left = (limit: unknown, used: unknown) => Math.max(0, Number(limit || 0) - Number(used || 0));
  const articlesLeft = left(plan.numberOfArticlesInPlan, plan.totalArticlesWritten);
  const wordsLeft = left(plan.numberOfWordsInPlan, plan.totalWordsWritten);
  if (articlesLeft === 0) {
    return { kind: 'limit-reached', message: "You've reached a limit for Updating Articles" };
  }
  if (wordsLeft === 0) {
    return { kind: 'limit-reached', message: "You've reached a limit of word count" };
  }
  if (rowCount > articlesLeft) {
    return { kind: 'too-many', articlesLeft };
  }
  return { kind: 'ok', articlesLeft };
}
